import { baselineFromCenter, baselineFromTop, faceHeight, lineHeight, textAscent, textDescent, wrapText } from './chartText.js'

/**
 * Word chart model (App\Services\Docx\Studio\Presentation\Charts\ChartReader)
 * → drawing primitives at the chart's size, in points, laid out the way
 * Office lays a 2-D column/line chart out: the plot area from its manual
 * layout, value axes on the server-resolved scale, category slots split by
 * gap width and overlap, labels stacked by the faces' own line metrics.
 *
 * Pure: text widths come from the `measure(text, style) → pt` callback, so
 * the geometry is unit-testable without a browser. Primitives:
 *   { kind: 'rect', x, y, w, h, fill, line, clip? }
 *   { kind: 'line', x1, y1, x2, y2, line, clip? }
 *   { kind: 'path', points: [[x, y]…], line, clip? }
 *   { kind: 'marker', symbol, x, y, size, fill, line }
 *   { kind: 'text', x, y, text, style, anchor: 'start'|'middle'|'end', rotate? }
 * Text x/y is the baseline origin; `rotate` (degrees) turns it about that point.
 */

/** Office's fixed distances, points (calibrated against Word's renders). */
export const METRICS = {
  // Major/minor tick mark length.
  tickLength: 3.75,
  // Value-axis label ↔ the outer end of the tick mark's room.
  valueLabelGap: 4.0,
  // Category label row ↔ axis line at c:lblOffset 0; each 100 % of label
  // offset adds categoryOffsetUnit (tick marks add nothing).
  categoryLabelGap: 2.0,
  categoryOffsetUnit: 3.0,
  // Extra space between multi-level category rows.
  categoryRowGap: 2.9,
  // Data label box insets around its text (Office's 3 pt / 1.5 pt).
  dataLabelPadX: 3.0,
  dataLabelPadY: 1.5,
  // Data label box ↔ the bar end/base or marker edge it labels.
  dataLabelOffset: 3.2,
  // Level labels outside a bar end or a point keep this much more distance.
  dataLabelOutside: 2.4,
  // Legend: the key (a bar's rectangle, a line's stroke) and the text after
  // it. A bar key is legendKeyRatio of the face's Windows height tall; in a
  // legend without line entries it is square and the text follows closer.
  legendKeyLength: 19.18,
  legendKeyRatio: 0.45,
  legendTextOffset: 21.55,
  legendKeyTextGap: 2.0,
  legendSquareTextGap: 1.8,
  // ... and when any line entry is dashed (Word experiments on chart 08).
  legendDashedKeyLength: 25.2,
  legendDashedTextOffset: 29.05,
  legendDashedKeyTextGap: 2.75,
  // Automatic legends: the box is legendSpread × its entries plus
  // legendPad, the spare split evenly before, between and after them; the
  // box sits legendNudge right of the chart's centre, its row legendEdge
  // above the bottom edge (fitted on the ten prod charts with one).
  legendSpread: 1.2,
  legendPad: 9.15,
  legendNudge: 1.76,
  legendEdge: 8.9,
  // A manually laid-out legend's entries sit this much right of the even split.
  legendShift: 2.0,
  // A manually sized legend draws only the rows that fit: each row needs
  // legendRowFit × the face's Windows height (Arial 9: 8.756 pt), n rows
  // showing from exactly n × that up; the entries of the rows past the
  // last one that fits are left out, one row is always drawn. Bisected on
  // Word renders of chart 08 with seven entries, the legend's c:h stepped
  // to 0.004 pt: 0.8708–0.8714 for Arial 9/12/20 and Calibri 9/12, for 1
  // to 7 rows, no fixed part.
  legendRowFit: 0.8712,
  // A top/bottom legend whose two widest entries do not fit in the chart's
  // width less this would be one column if Office laid it out itself, and
  // then lists stacked columns top-down like a side legend — even where a
  // manual layout later gives it two columns. Bracketed by Word renders of
  // one chart at two widths with the long label lengthened step by step:
  // [43.7, 47.5) at 445.2 pt and [38.6, 47.5) at 378 pt wide.
  legendColumnRoom: 45.6,
  // An automatically placed value-axis title's box keeps this much from its
  // tick labels: Word sits a right-hand title closer than a left-hand one
  // (median text residual of the eleven auto right titles was 2.4 pt).
  axisTitleGapLeft: 4.0,
  axisTitleGapRight: 1.6,
  // Superscript / subscript runs (a:rPr@baseline) are drawn at this share of their size.
  scriptScale: 2 / 3,
  // Title boxes' inner padding along / across the text; a chart title's top inset.
  titlePadX: 3.0,
  titlePadY: 1.2,
  chartTitlePadY: 0.24,
  // A chart title wraps at this share of the chart's width.
  titleMaxWidth: 0.8,
  // Automatic plot area (autoPlotRect).
  autoMargin: 11.2,
  autoTitleGap: 9.06,
  autoLegendGap: 11.87,
  autoValueLabelPush: 3.11,
  autoCategoryInset: 4.08,
  autoAxisTitleGap: 8.57,
}

const EPSILON = 1e-9

export function layoutChart(model, measure) {
  const W = model.widthPt
  const H = model.heightPt
  const items = []
  const plot = plotRect(model, W, H, measure)
  const axes = model.axes ?? {}
  const groups = model.groups ?? []

  // Chart area, plot area.
  items.push({ kind: 'rect', x: 0, y: 0, w: W, h: H, fill: model.space?.fill ?? null, line: model.space?.line ?? null, frame: true })
  if (model.plot?.fill || model.plot?.line) {
    items.push({ kind: 'rect', x: plot.x, y: plot.y, w: plot.w, h: plot.h, fill: model.plot.fill, line: model.plot.line })
  }
  items.push({ kind: 'clip', id: 'plot', x: plot.x, y: plot.y, w: plot.w, h: plot.h })

  const scales = buildScales(model, plot)

  // Gridlines (behind the series).
  for (const axis of Object.values(axes)) {
    const scale = scales[axis.id]
    if (!scale || !axis.majorGridlines) continue
    if (axis.kind === 'val') {
      for (const tick of axis.ticks ?? []) {
        const y = scale.position(tick.value)
        items.push({ kind: 'line', x1: plot.x, y1: y, x2: plot.x + plot.w, y2: y, line: axis.majorGridlines })
      }
    } else {
      for (const x of scale.boundaries()) {
        items.push({ kind: 'line', x1: x, y1: plot.y, x2: x, y2: plot.y + plot.h, line: axis.majorGridlines })
      }
    }
  }

  // Bars, then the axis lines, then lines and markers (Office's z-order).
  const labelBoxes = []
  for (const group of groups) {
    if (group.type === 'bar') drawBars(group, scales, items, labelBoxes, model, measure)
  }
  for (const axis of Object.values(axes)) drawAxisLine(axis, scales, plot, items)
  for (const group of groups) {
    if (group.type === 'line') drawLines(group, scales, items, labelBoxes, model, measure)
  }

  for (const axis of Object.values(axes)) drawAxisLabels(axis, scales, plot, items, measure)
  for (const box of labelBoxes) items.push(...box)

  for (const axis of Object.values(axes)) {
    if (axis.title && !axis.deleted) drawAxisTitle(axis, scales, plot, items, measure, W, H)
  }
  if (model.title) drawChartTitle(model.title, items, measure, W, H)
  if (model.legend) drawLegend(model.legend, model, items, measure, W, H, plot)
  for (const shape of model.shapes ?? []) drawShape(shape, items, measure, W, H)

  return { width: W, height: H, plot, items }
}

// ------------------------------------------------------------------ plot

function plotRect(model, W, H, measure) {
  const layout = model.plot?.layout
  if (layout && layout.w !== undefined && layout.h !== undefined && layout.x !== undefined && layout.y !== undefined) {
    const box = { x: layout.x * W, y: layout.y * H, w: layout.w * W, h: layout.h * H }
    if (model.plot.target === 'inner') return box
    return innerOf(box, model, measure)
  }
  return autoPlotRect(model, W, H, measure)
}

/**
 * Office's automatic plot area: the chart inset by autoMargin on every side,
 * then pushed in by what stands outside it — the title and a top/bottom
 * legend (their lines plus a gap), tick labels and axis titles per axis.
 * Fitted on Word's renders (the two prod charts without a manual layout,
 * and variants of one with the title, legend and labels removed in turn).
 */
function autoPlotRect(model, W, H, measure) {
  const inset = METRICS.autoMargin
  const box = { left: inset, top: inset, right: W - inset, bottom: H - inset }
  const title = model.title
  if (title && !title.overlay && (title.x === null || title.x === undefined)) {
    box.top += textBlock(title.paragraphs, measure, titleWrapWidth(W)).height + METRICS.autoTitleGap
  }
  const legend = model.legend
  if (legend && !legend.overlay && !legend.layout && legendEntries(legend, model).length) {
    const push = faceHeight(legend.text) + METRICS.autoLegendGap
    if (legend.position === 'b') box.bottom -= push
    else if (legend.position === 't') box.top += push
    else {
      const entries = legendEntries(legend, model)
      const widest = Math.max(...entries.map((entry) => measure(entry.label, legend.text))) + legendKeys(entries, legend.text).textOffset
      if (legend.position === 'l') box.left += widest + METRICS.autoLegendGap
      else box.right -= widest + METRICS.autoLegendGap
    }
  }
  for (const axis of Object.values(model.axes ?? {})) {
    if (axis.deleted) continue
    let push = 0
    if (axis.tickLblPos !== 'none') {
      if (axis.kind === 'val') {
        push = Math.max(0, ...(axis.ticks ?? []).map((tick) => measure(tick.label, axis.text))) + METRICS.autoValueLabelPush
      } else {
        const offset = (axis.lblOffset ?? 100) / 100
        const rows = Math.max(1, axis.levels?.length ?? 1)
        push = METRICS.categoryLabelGap + offset * METRICS.categoryOffsetUnit + rows * faceHeight(axis.text) - METRICS.autoCategoryInset
      }
    }
    if (axis.title) push += faceHeight(axis.title.paragraphs?.[0]?.style ?? axis.text) + METRICS.autoAxisTitleGap
    if (axis.position === 'l') box.left += push
    else if (axis.position === 'r') box.right -= push
    else if (axis.position === 'b') box.bottom -= push
    else if (axis.position === 't') box.top += push
  }
  return { x: box.left, y: box.top, w: Math.max(box.right - box.left, 10), h: Math.max(box.bottom - box.top, 10) }
}

/** The inner plot box of an outer one: minus tick labels and axis titles. */
function innerOf(box, model, measure) {
  const inner = { ...box }
  for (const axis of Object.values(model.axes ?? {})) {
    if (axis.deleted || axis.tickLblPos === 'none') continue
    const labels =
      axis.kind === 'val'
        ? Math.max(0, ...(axis.ticks ?? []).map((tick) => measure(tick.label, axis.text))) + METRICS.valueLabelGap + METRICS.tickLength
        : (axis.levels?.length || 1) * lineHeight(axis.text) + METRICS.categoryLabelGap
    const extent = labels + (axis.title ? lineHeight(axis.title.paragraphs?.[0]?.style ?? axis.text) + 4 : 0)
    if (axis.position === 'l') {
      inner.x += extent
      inner.w -= extent
    } else if (axis.position === 'r') {
      inner.w -= extent
    } else if (axis.position === 'b') {
      inner.h -= extent
    } else if (axis.position === 't') {
      inner.y += extent
      inner.h -= extent
    }
  }
  inner.w = Math.max(inner.w, 10)
  inner.h = Math.max(inner.h, 10)
  return inner
}

// ---------------------------------------------------------------- scales

/**
 * Per axis id: value axes map a value to y; category axes map a slot to the
 * x of its centre (between) or tick (midCat) and list slot boundaries.
 */
function buildScales(model, plot) {
  const scales = {}
  const axes = model.axes ?? {}
  for (const axis of Object.values(axes)) {
    if (axis.kind === 'val') {
      const vertical = axis.position === 'l' || axis.position === 'r'
      const min = axis.min ?? 0
      const max = axis.max ?? 1
      const span = max - min || 1
      scales[axis.id] = {
        axis,
        min,
        max,
        vertical,
        position: (value) => {
          let fraction = (value - min) / span
          if (axis.reversed) fraction = 1 - fraction
          return vertical ? plot.y + plot.h * (1 - fraction) : plot.x + plot.w * fraction
        },
      }
    }
  }
  const crossBetween = (axis) => (Object.values(axes).find((other) => other.kind === 'val' && other.crossAx === axis.id)?.crossBetween ?? 'between') === 'between'
  // A combo chart whose bars sit on a date axis "on tick marks" and whose
  // lines sit on a second date axis "between" them: Word puts both on one
  // date scale that starts at the plot's left edge and ends half a base
  // unit after the last date (measured on the four such prod charts).
  const dateAxes = Object.values(axes).filter((axis) => axis.kind === 'date')
  const sharedDates = dateAxes.some((axis) => !crossBetween(axis)) && dateAxes.some((axis) => crossBetween(axis))
  const sharedCount = Math.max(1, ...dateAxes.filter((axis) => !crossBetween(axis)).map((axis) => axis.count ?? 1))
  for (const axis of Object.values(axes)) {
    if (axis.kind === 'val') continue
    const shared = sharedDates && axis.kind === 'date'
    const between = shared ? false : crossBetween(axis)
    const count = Math.max(1, axis.count ?? 1)
    const slots = shared ? Math.max(0.5, sharedCount - 0.5) : between ? count : Math.max(1, count - 1)
    const width = plot.w / slots
    const flip = (x) => (axis.reversed ? plot.x + plot.x + plot.w - x : x)
    scales[axis.id] = {
      axis,
      between,
      count,
      slotWidth: width,
      center: (slot) => flip(between ? plot.x + (slot + 0.5) * width : plot.x + slot * width),
      start: (slot) => flip(between ? plot.x + slot * width : plot.x + (slot - 0.5) * width),
      boundaries: () => {
        const out = []
        if (between) for (let index = 0; index <= count; index++) out.push(flip(plot.x + index * width))
        else for (let index = 0; index < count; index++) out.push(flip(plot.x + index * width))
        return out
      },
    }
  }
  return scales
}

/** The value where category axis `axis` crosses its value axis (bars grow from it). */
function crossValue(categoryAxis, valueScale) {
  if (!valueScale) return 0
  const { min, max } = valueScale
  if (categoryAxis?.crossesAt !== null && categoryAxis?.crossesAt !== undefined) return clamp(categoryAxis.crossesAt, min, max)
  if (categoryAxis?.crosses === 'max') return max
  if (categoryAxis?.crosses === 'min') return min
  return clamp(0, min, max)
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

// ---------------------------------------------------------------- series

function drawBars(group, scales, items, labelBoxes, model, measure) {
  const categories = scales[group.catAx]
  const values = scales[group.valAx]
  if (!categories || !values) return
  const series = group.series ?? []
  const gap = (group.gapWidth ?? 150) / 100
  const stacked = group.grouping === 'stacked' || group.grouping === 'percentStacked'
  const overlap = stacked ? 1 : (group.overlap ?? 0) / 100
  const n = stacked ? 1 : series.length
  const barWidth = categories.slotWidth / (n - (n - 1) * overlap + gap)
  const base = crossValue(categories.axis, values)
  const positive = []
  const negative = []

  series.forEach((entry, seriesIndex) => {
    const offset = stacked ? 0 : seriesIndex * barWidth * (1 - overlap)
    const geometry = []
    entry.values.forEach((value, index) => {
      const slot = entry.slots?.[index] ?? index
      if (value === null || value === undefined || slot === null) return
      let from = base
      let to = value
      if (stacked) {
        const stack = value >= 0 ? positive : negative
        from = stack[slot] ?? 0
        to = from + value
        stack[slot] = to
      }
      const left = categories.start(slot) + (gap * barWidth) / 2 + offset
      const y1 = values.position(from)
      const y2 = values.position(to)
      const rect = { x: left, y: Math.min(y1, y2), w: barWidth, h: Math.abs(y2 - y1) }
      geometry[index] = { ...rect, up: to >= from }
      items.push({ kind: 'rect', ...rect, fill: entry.fill, line: entry.line, clip: 'plot' })
    })
    if (entry.labels) labelBoxes.push(barLabels(entry, geometry, model, measure))
  })
}

function drawLines(group, scales, items, labelBoxes, model, measure) {
  const categories = scales[group.catAx]
  const values = scales[group.valAx]
  if (!categories || !values) return
  const blanks = model.dispBlanksAs ?? 'gap'
  for (const entry of group.series ?? []) {
    const points = []
    let run = []
    entry.values.forEach((value, index) => {
      const slot = entry.slots?.[index] ?? index
      if (slot === null) return
      if (value === null || value === undefined) {
        if (blanks === 'zero') value = 0
        else {
          if (blanks !== 'span' && run.length) {
            points.push(run)
            run = []
          }
          return
        }
      }
      run.push([categories.center(slot), values.position(value), index])
    })
    if (run.length) points.push(run)
    if (entry.line) {
      for (const segment of points) {
        if (segment.length > 1) items.push({ kind: 'path', points: segment.map(([x, y]) => [x, y]), line: entry.line, clip: 'plot' })
      }
    }
    const geometry = []
    for (const segment of points) {
      for (const [x, y, index] of segment) {
        geometry[index] = { x, y }
        if (entry.marker) items.push({ kind: 'marker', symbol: entry.marker.symbol, x, y, size: entry.marker.size, fill: entry.marker.fill, line: entry.marker.line })
      }
    }
    if (entry.labels) labelBoxes.push(pointLabels(entry, geometry, model, measure))
  }
}

// ----------------------------------------------------------- data labels

/** A data label's text box: width/height as drawn (rotation applied). */
function labelBox(text, style, rotation, measure) {
  const lines = String(text).split('\n')
  const width = Math.max(...lines.map((line) => measure(line, style))) + 2 * METRICS.dataLabelPadX
  const height = lines.length * lineHeight(style) + 2 * METRICS.dataLabelPadY
  const turned = rotation !== null && rotation !== undefined && Math.abs(Math.abs(rotation) - 90) < 1
  return { lines, width: turned ? height : width, height: turned ? width : height, textWidth: width, textHeight: height, turned }
}

function barLabels(entry, geometry, model, measure) {
  const out = []
  const labels = entry.labels
  for (const item of labels.items) {
    const bar = geometry[item.index]
    if (!bar) continue
    const style = item.style ?? labels.text
    const box = labelBox(item.text, style, labels.rotation, measure)
    const position = item.position ?? labels.position
    const cx = bar.x + bar.w / 2
    let cy
    const top = bar.up ? bar.y : bar.y + bar.h
    const bottom = bar.up ? bar.y + bar.h : bar.y
    const direction = bar.up ? 1 : -1
    if (position === 'inBase') cy = bottom - direction * (box.height / 2 + METRICS.dataLabelOffset)
    else if (position === 'inEnd') cy = top + direction * (box.height / 2 + METRICS.dataLabelOffset)
    else if (position === 'outEnd') cy = top - direction * (box.height / 2 + METRICS.dataLabelOffset + (box.turned ? 0 : METRICS.dataLabelOutside))
    else cy = bar.y + bar.h / 2
    out.push(...labelText(box, style, cx + (item.dx ?? 0) * model.widthPt, cy + (item.dy ?? 0) * model.heightPt, labels, position))
  }
  return out
}

function pointLabels(entry, geometry, model, measure) {
  const out = []
  const labels = entry.labels
  const radius = entry.marker ? entry.marker.size / 2 : 0
  for (const item of labels.items) {
    const point = geometry[item.index]
    if (!point) continue
    const style = item.style ?? labels.text
    const box = labelBox(item.text, style, labels.rotation, measure)
    const position = item.position ?? labels.position
    let cx = point.x
    let cy = point.y
    const offset = radius + METRICS.dataLabelOffset + (box.turned ? 0 : METRICS.dataLabelOutside)
    if (position === 't') cy -= offset + box.height / 2
    else if (position === 'b') cy += offset + box.height / 2
    else if (position === 'l') cx -= offset + box.width / 2
    else if (position === 'r') cx += offset + box.width / 2
    out.push(...labelText(box, style, cx + (item.dx ?? 0) * model.widthPt, cy + (item.dy ?? 0) * model.heightPt, labels, position))
  }
  return out
}

/** Text primitives for a label box centred on (cx, cy). */
function labelText(box, style, cx, cy, labels, detail = null) {
  const out = []
  if (labels.fill || labels.line) {
    out.push({ kind: 'rect', x: cx - box.width / 2, y: cy - box.height / 2, w: box.width, h: box.height, fill: labels.fill, line: labels.line })
  }
  const advance = lineHeight(style)
  const blockHeight = box.lines.length * advance
  box.lines.forEach((line, index) => {
    const offsetY = -blockHeight / 2 + index * advance + baselineOffset(style)
    if (box.turned) {
      // Laid out level, then turned about the box centre.
      out.push({ kind: 'text', role: 'dataLabel', detail, x: cx, y: cy + offsetY, text: line, style, anchor: 'middle', rotate: labels.rotation, ox: cx, oy: cy })
    } else {
      out.push({ kind: 'text', role: 'dataLabel', detail, x: cx, y: cy + offsetY, text: line, style, anchor: 'middle' })
    }
  })
  return out
}

/** Distance from a line box's top to its baseline: the line gap sits above the ascent. */
function baselineOffset(style) {
  return lineHeight(style) - textDescent(style)
}

// ------------------------------------------------------------------ axes

function axisCrossing(axis, scales, plot) {
  const other = Object.values(scales).find((scale) => scale.axis.id === axis.crossAx)
  if (axis.kind === 'val') {
    // Where the value axis crosses its category axis: an end of the plot.
    const reversed = other?.axis?.reversed
    const atMax = axis.crosses === 'max'
    const right = atMax !== Boolean(reversed)
    return axis.position === 'l' || axis.position === 'r'
      ? right
        ? plot.x + plot.w
        : plot.x
      : right
        ? plot.y
        : plot.y + plot.h
  }
  if (other) return other.position(crossValue(axis, other))
  return axis.position === 't' ? plot.y : plot.y + plot.h
}

function drawAxisLine(axis, scales, plot, items) {
  if (axis.deleted || !axis.line) return
  const at = axisCrossing(axis, scales, plot)
  if (axis.kind === 'val') {
    items.push({ kind: 'line', x1: at, y1: plot.y, x2: at, y2: plot.y + plot.h, line: axis.line })
  } else {
    items.push({ kind: 'line', x1: plot.x, y1: at, x2: plot.x + plot.w, y2: at, line: axis.line })
  }
}

function tickSpan(mark, outward) {
  const length = METRICS.tickLength
  if (mark === 'out') return [0, outward * length]
  if (mark === 'in') return [-outward * length, 0]
  if (mark === 'cross') return [-length, length]
  return null
}

function drawAxisLabels(axis, scales, plot, items, measure) {
  if (axis.deleted) return
  const scale = scales[axis.id]
  if (!scale) return
  const at = axisCrossing(axis, scales, plot)
  if (axis.kind === 'val') drawValueAxis(axis, scale, at, plot, items)
  else drawCategoryAxis(axis, scale, at, plot, items, measure)
}

function drawValueAxis(axis, scale, at, plot, items) {
  const outward = axis.position === 'r' ? 1 : -1
  const ticks = tickSpan(axis.majorTickMark, outward)
  if (ticks && axis.line) {
    for (const tick of axis.ticks ?? []) {
      const y = scale.position(tick.value)
      items.push({ kind: 'line', x1: at + ticks[0], y1: y, x2: at + ticks[1], y2: y, line: axis.line })
    }
  }
  if (axis.tickLblPos === 'none') return
  let side = outward
  let anchorX = at
  if (axis.tickLblPos === 'low') {
    side = -1
    anchorX = plot.x
  } else if (axis.tickLblPos === 'high') {
    side = 1
    anchorX = plot.x + plot.w
  }
  // Word keeps the tick mark's room whether or not the axis draws one.
  const x = anchorX + side * (METRICS.tickLength + METRICS.valueLabelGap)
  const style = axis.text
  for (const tick of axis.ticks ?? []) {
    const y = scale.position(tick.value)
    items.push({
      kind: 'text', role: 'valLabel',
      x,
      y: y + baselineFromCenter(style),
      text: tick.label,
      style,
      anchor: side < 0 ? 'end' : 'start',
    })
  }
}

function drawCategoryAxis(axis, scale, at, plot, items, measure) {
  const outward = axis.position === 't' ? -1 : 1
  const ticks = tickSpan(axis.majorTickMark, outward)
  const levels = axis.levels ?? []
  const multiLevel = levels.length > 1
  const style = axis.text
  const row = lineHeight(style)
  const skipMarks = Math.max(1, axis.tickMarkSkip ?? 1)

  if (ticks && axis.line && !multiLevel) {
    scale.boundaries().forEach((x, index) => {
      if (index % skipMarks !== 0) return
      items.push({ kind: 'line', x1: x, y1: at + ticks[0], x2: x, y2: at + ticks[1], line: axis.line })
    })
  }
  if (axis.tickLblPos === 'none' || levels.length === 0) return

  const labelsAt = axis.tickLblPos === 'low' ? plot.y + plot.h : axis.tickLblPos === 'high' ? plot.y : at
  // Word places category labels by c:lblOffset alone: tick marks move them
  // neither in a manual nor in an automatic layout.
  const offset = (axis.lblOffset ?? 100) / 100
  let y = labelsAt + outward * (METRICS.categoryLabelGap + offset * METRICS.categoryOffsetUnit)
  let rotation = axis.rotation
  let step = Math.max(1, axis.tickLblSkip ?? 1)
  const labelSlots = scale.slotWidth * (axis.labelStep ?? 1)
  if ((rotation === null || rotation === undefined) && !multiLevel) {
    // Automatic text direction: a label word that does not fit its slot
    // turns the whole row a quarter turn (Office 2007 charts).
    const widestWord = Math.max(0, ...levels[0].filter((span, index) => index % step === 0).flatMap((span) => String(span.label).split(/ +/).map((word) => measure(word, style))))
    if (widestWord > labelSlots * step) rotation = -90
  }
  if (rotation !== null && rotation !== undefined && Math.abs(rotation) > 0.5 && (axis.tickLblSkip ?? null) === null && (axis.labelStep ?? null) === null) {
    // Automatic label interval: turned labels need a line's height each.
    step = Math.max(1, Math.ceil(faceHeight(style) / labelSlots - 1e-6))
  }

  const separators = new Map() // boundary slot → bottom of the outermost row it divides
  levels.forEach((level, depth) => {
    const spans = level
    const top = y
    let rowHeight = row
    if (depth === 0) {
      const slotSpan = (axis.labelStep ?? 1) * step
      const available = scale.slotWidth * slotSpan
      const turned = rotation !== null && rotation !== undefined && Math.abs(rotation) > 0.5
      if (turned) {
        // Rotated labels: anchored at their end just below the axis.
        let tallest = 0
        spans.forEach((span, index) => {
          if (index % step !== 0) return
          const width = measure(span.label, style)
          tallest = Math.max(tallest, Math.abs(Math.sin((rotation * Math.PI) / 180)) * width + Math.abs(Math.cos((rotation * Math.PI) / 180)) * row)
          const cx = (scale.center(span.start) + scale.center(span.end)) / 2
          // Laid out level with its end (start, turned clockwise) on the slot
          // centre just below the axis, then turned about that point; a label
          // turned less than a quarter drops until its raised corner clears
          // the axis (chart 33 at -24°: 4.5 pt in Word).
          const pivot = top + (row / 2) * Math.abs(Math.cos((rotation * Math.PI) / 180))
          items.push({ kind: 'text', role: 'catLabel', target: categoryTarget(axis, 0, span), x: cx, y: pivot - row / 2 + baselineOffset(style), text: span.label, style, anchor: rotation < 0 ? 'end' : 'start', rotate: rotation, ox: cx, oy: pivot })
        })
        rowHeight = tallest
      } else {
        let lines = 1
        const wrapped = spans.map((span) => {
          const parts = wrapText(span.label, style, Math.max(available - 2, 1), measure)
          lines = Math.max(lines, parts.length)
          return parts
        })
        spans.forEach((span, index) => {
          if (index % step !== 0) return
          const cx = (scale.center(span.start) + scale.center(span.end)) / 2
          wrapped[index].forEach((part, line) => {
            items.push({ kind: 'text', role: 'catLabel', target: categoryTarget(axis, 0, span), x: cx, y: top + line * row + baselineFromTop(style), text: part, style, anchor: 'middle' })
          })
        })
        rowHeight = lines * row
      }
    } else {
      spans.forEach((span) => {
        const cx = (scale.start(span.start) + scale.start(span.end + 1)) / 2
        const parts = wrapText(span.label, style, Math.max(scale.slotWidth * (span.end - span.start + 1) - 2, 1), measure)
        parts.forEach((part, line) => {
          items.push({ kind: 'text', role: 'catLabel2', target: categoryTarget(axis, depth, span), x: cx, y: top + line * row + baselineFromTop(style), text: part, style, anchor: 'middle' })
        })
        rowHeight = Math.max(rowHeight, parts.length * row)
      })
    }
    y = top + rowHeight + METRICS.categoryRowGap
    if (multiLevel) {
      for (const span of spans) {
        separators.set(span.start, y)
        separators.set(span.end + 1, y)
      }
      separators.set(0, y)
      separators.set(axis.count, y)
    }
  })

  // Level separators, each from the axis down through the outermost level
  // it divides; the innermost level's own ones only where the axis draws
  // tick marks (Word leaves them out at c:majorTickMark none).
  if (multiLevel && axis.line) {
    const innerOnly = new Set(levels[0].flatMap((span) => [span.start, span.end + 1]))
    const outer = new Set(levels.slice(1).flatMap((level) => level.flatMap((span) => [span.start, span.end + 1])).concat([0, axis.count]))
    for (const [boundary, bottom] of separators) {
      if (!outer.has(boundary) && (!innerOnly.has(boundary) || axis.majorTickMark === 'none')) continue
      const x = scale.start(boundary)
      items.push({ kind: 'line', x1: x, y1: at, x2: x, y2: bottom, line: axis.line })
    }
  }
}

/**
 * The edit target of a category label (RED-317 PR 3d-A): its level and the
 * row it starts at. Only a text category axis maps a label to a row; date
 * labels are month slots.
 */
function categoryTarget(axis, depth, span) {
  return axis.kind === 'cat' ? `cat:${depth}:${span.start}` : undefined
}

function drawAxisTitle(axis, scales, plot, items, measure, W, H) {
  const title = axis.title
  const vertical = axis.position === 'l' || axis.position === 'r'
  const rotation = title.rotation ?? (vertical ? -90 : 0)
  const block = textBlock(title.paragraphs, measure, null)
  const turned = Math.abs(Math.abs(rotation) - 90) < 1
  const boxW = (turned ? block.height : block.width) + 2 * METRICS.titlePadY
  const boxH = (turned ? block.width : block.height) + 2 * METRICS.titlePadX
  let x
  let y
  if (title.x !== null && title.x !== undefined && title.y !== null && title.y !== undefined) {
    x = title.x * W
    y = title.y * H
  } else if (vertical) {
    y = plot.y + plot.h / 2 - boxH / 2
    const labels = Math.max(0, ...(axis.ticks ?? []).map((tick) => measure(tick.label, axis.text)))
    const reach = METRICS.tickLength + METRICS.valueLabelGap + labels
    x = axis.position === 'l' ? plot.x - reach - METRICS.axisTitleGapLeft - boxW : plot.x + plot.w + reach + METRICS.axisTitleGapRight
  } else {
    x = plot.x + plot.w / 2 - boxW / 2
    y = plot.y + plot.h + 2 * lineHeight(axis.text) + 6
  }
  pushBlock(block, x, y, boxW, boxH, rotation, items, title, 'axisTitle')
}

/** Office wraps a chart title's box at 80 % of the chart's width (its text inside the box's padding). */
function titleWrapWidth(W) {
  return METRICS.titleMaxWidth * W - 2 * METRICS.titlePadX
}

function drawChartTitle(title, items, measure, W, H) {
  const block = textBlock(title.paragraphs, measure, titleWrapWidth(W))
  const boxW = block.width + 2 * METRICS.titlePadX
  const boxH = block.height + 2 * METRICS.chartTitlePadY
  const x = title.x !== null && title.x !== undefined ? title.x * W : W / 2 - boxW / 2
  const y = title.y !== null && title.y !== undefined ? title.y * H : 7.2
  pushBlock(block, x, y, boxW, boxH, title.rotation ?? 0, items, title)
}

/**
 * Paragraphs → measured lines: [{ runs: [{text, style, width}], width, height, ascent, align }].
 * `maxWidth` wraps (text boxes); null keeps each paragraph on one line.
 * A paragraph without a:pPr@algn takes `defaultAlign`: centred in titles,
 * left in a text box (DrawingML's own default).
 */
function textBlock(paragraphs, measure, maxWidth, defaultAlign = 'ctr') {
  const lines = []
  for (const paragraph of paragraphs ?? []) {
    const align = paragraph.align ?? defaultAlign
    const runs = (paragraph.runs ?? []).flatMap((run) => {
      const { style, rise } = scriptRun(run.style)
      return String(run.text).split(/(\n)/).map((text) => ({ text, style, rise }))
    })
    let line = { runs: [], align }
    const flush = () => {
      lines.push(finishLine(line, paragraph.style))
      line = { runs: [], align }
    }
    for (const run of runs) {
      if (run.text === '\n') {
        flush()
        continue
      }
      if (maxWidth === null) {
        line.runs.push({ text: run.text, style: run.style, rise: run.rise })
        continue
      }
      // Word wrap inside a text box, run by run: after a space, and after a
      // hyphen inside a word ("2020-2025" breaks as Word breaks it).
      const words = run.text.split(/(?<= )|(?<=\S-)(?=[^\s-])/)
      for (const word of words) {
        const used = line.runs.reduce((sum, part) => sum + measure(part.text, part.style), 0)
        const width = measure(word.trimEnd(), run.style)
        if (used > 0 && used + width > maxWidth + 0.01) {
          flush()
        }
        const last = line.runs[line.runs.length - 1]
        if (last && last.style === run.style) last.text += word
        else line.runs.push({ text: word, style: run.style, rise: run.rise })
      }
    }
    flush()
  }
  for (const line of lines) {
    line.width = line.runs.reduce((sum, run, index) => sum + measure(index === line.runs.length - 1 ? run.text.trimEnd() : run.text, run.style), 0)
    for (const run of line.runs) run.width = measure(run.text, run.style)
  }
  const width = Math.max(0, ...lines.map((line) => line.width))
  const height = lines.reduce((sum, line) => sum + line.height, 0)
  return { lines, width, height }
}

const scriptStyles = new WeakMap()

/**
 * A run raised or lowered by a:rPr@baseline (percent of its size): Office
 * draws it at two thirds of the size (Word's PDF: a 9 pt title's "m²" has a
 * 6 pt "2", 30 % up). The style object is cached so a run's words still
 * merge by identity.
 */
function scriptRun(style) {
  if (!style?.baseline) return { style, rise: 0 }
  if (!scriptStyles.has(style)) scriptStyles.set(style, { ...style, size: Math.round(style.size * METRICS.scriptScale * 100) / 100, baseline: 0 })
  return { style: scriptStyles.get(style), rise: (style.baseline / 100) * style.size }
}

function finishLine(line, fallback) {
  const styles = line.runs.length ? line.runs.map((run) => run.style) : [fallback]
  const height = Math.max(...styles.map((style) => lineHeight(style)))
  const descent = Math.max(...styles.map((style) => textDescent(style)))
  return { ...line, height, baseline: height - descent }
}

/**
 * Draw a measured block inside a box at (x, y) of size boxW × boxH (as it
 * stands on the page), rotated by `rotation` about the box centre.
 */
function pushBlock(block, x, y, boxW, boxH, rotation, items, format, role = 'title') {
  if (format?.fill || format?.line) items.push({ kind: 'rect', x, y, w: boxW, h: boxH, fill: format.fill, line: format.line })
  const cx = x + boxW / 2
  const cy = y + boxH / 2
  let top = -block.height / 2
  for (const line of block.lines) {
    const left = line.align === 'l' ? -block.width / 2 : line.align === 'r' ? block.width / 2 - line.width : -line.width / 2
    let offset = left
    for (const run of line.runs) {
      if (run.text !== '') {
        items.push({ kind: 'text', role, target: format?.key, x: cx + offset, y: cy + top + line.baseline - (run.rise ?? 0), text: run.text, style: run.style, anchor: 'start', rotate: rotation || undefined, ox: cx, oy: cy })
      }
      offset += run.width
    }
    top += line.height
  }
}

// ---------------------------------------------------------------- legend

/**
 * The legend's entries in Office's order: a vertical legend (right/left, or
 * a top/bottom one that would stack in one column: stackedDown) lists
 * stacked columns top-down, i.e. their series in reverse.
 */
function legendEntries(legend, model, stackedDown = false) {
  const entries = [...(legend.entries ?? [])]
  if (legend.position !== 'r' && legend.position !== 'l' && !stackedDown) return entries
  const out = []
  for (const group of model.groups ?? []) {
    const ids = new Set((group.series ?? []).map((series) => series.idx))
    const own = entries.filter((entry) => ids.has(entry.series))
    out.push(...(group.type === 'bar' && group.grouping === 'stacked' ? own.reverse() : own))
  }
  return out.length === entries.length ? out : entries
}

/**
 * The legend's key geometry: with any line entry every key is a stroke's
 * length wide, longer when one of those lines is dashed (Word: 25.2 pt
 * instead of 19.2, the text 7.5 pt further on, for any dash and width);
 * without one, keys are squares.
 */
function legendKeys(entries, style) {
  const keyHeight = METRICS.legendKeyRatio * faceHeight(style)
  const lines = entries.filter((entry) => entry.type === 'line')
  if (!lines.length) return { keyHeight, keyLength: keyHeight, textOffset: keyHeight + METRICS.legendSquareTextGap, keyOffset: 0 }
  const dashed = lines.some((entry) => entry.line && entry.line.dash && entry.line.dash !== 'solid')
  const keyLength = dashed ? METRICS.legendDashedKeyLength : METRICS.legendKeyLength
  const textOffset = dashed ? METRICS.legendDashedTextOffset : METRICS.legendTextOffset
  const gap = dashed ? METRICS.legendDashedKeyTextGap : METRICS.legendKeyTextGap
  return { keyHeight, keyLength, textOffset, keyOffset: textOffset - gap - keyLength }
}

function drawLegend(legend, model, items, measure, W, H, plot) {
  const style = legend.text
  const listed = legendEntries(legend, model)
  if (!listed.length) return
  const row = lineHeight(style)
  const { keyHeight, keyLength, textOffset, keyOffset } = legendKeys(listed, style)
  const widest = Math.max(...listed.map((entry) => textOffset + measure(entry.label, style)))
  const oneColumn = listed.length > 1 && 2 * widest > W - METRICS.legendColumnRoom
  const entries = oneColumn ? legendEntries(legend, model, true) : listed
  const widths = entries.map((entry) => textOffset + measure(entry.label, style))
  const placed = [] // { entry, x, center }

  if (legend.layout && legend.layout.w !== undefined && legend.layout.h !== undefined) {
    const box = { x: legend.layout.x * W, y: legend.layout.y * H, w: legend.layout.w * W, h: legend.layout.h * H }
    if (legend.fill || legend.line) items.push({ kind: 'rect', ...box, fill: legend.fill, line: legend.line })
    let columns = Math.max(1, Math.min(entries.length, Math.floor((box.w + EPSILON) / widest)))
    let rows = Math.ceil(entries.length / columns)
    columns = Math.ceil(entries.length / rows)
    // Only the rows that fit in the box's height are drawn (legendRowFit).
    rows = Math.min(rows, Math.max(1, Math.floor((box.h + EPSILON) / (METRICS.legendRowFit * faceHeight(style)))))
    const shown = entries.slice(0, rows * columns)
    if (rows === 1) {
      // One row: entries at their own widths, the spare width spread evenly.
      const spare = (box.w - widths.slice(0, shown.length).reduce((sum, width) => sum + width, 0)) / (shown.length + 1)
      let x = box.x + spare
      shown.forEach((entry, index) => {
        placed.push({ entry, x: x + METRICS.legendShift, center: box.y + box.h / 2 })
        x += widths[index] + spare
      })
    } else {
      // A grid of equal cells filling the box; the entries' common width
      // centred in each cell, entries left-aligned within it.
      const cellW = box.w / columns
      const cellH = box.h / rows
      shown.forEach((entry, index) => {
        const column = index % columns
        const rowIndex = Math.floor(index / columns)
        placed.push({ entry, x: box.x + column * cellW + (cellW - widest) / 2 + METRICS.legendShift, center: box.y + (rowIndex + 0.5) * cellH })
      })
    }
  } else {
    const horizontal = legend.position === 'b' || legend.position === 't'
    if (horizontal) {
      const content = widths.reduce((sum, width) => sum + width, 0)
      const boxW = METRICS.legendSpread * content + METRICS.legendPad
      const gap = (boxW - content) / (entries.length + 1)
      let x = (W - boxW) / 2 + METRICS.legendNudge + gap
      const center = legend.position === 'b' ? H - METRICS.legendEdge - row / 2 : METRICS.legendEdge + row / 2
      entries.forEach((entry, index) => {
        placed.push({ entry, x, center })
        x += widths[index] + gap
      })
    } else {
      const x = legend.position === 'l' ? METRICS.legendEdge : W - METRICS.legendEdge - widest
      const top = plot.y + plot.h / 2 - (entries.length * row) / 2
      entries.forEach((entry, index) => placed.push({ entry, x, center: top + (index + 0.5) * row }))
    }
  }

  for (const { entry, x, center } of placed) {
    const keyX = x + keyOffset
    if (entry.type === 'line') {
      if (entry.line) items.push({ kind: 'line', x1: keyX, y1: center, x2: keyX + keyLength, y2: center, line: entry.line })
      if (entry.marker) items.push({ kind: 'marker', symbol: entry.marker.symbol, x: keyX + keyLength / 2, y: center, size: entry.marker.size, fill: entry.marker.fill, line: entry.marker.line })
    } else {
      items.push({ kind: 'rect', x: keyX, y: center - keyHeight / 2, w: keyLength, h: keyHeight, fill: entry.fill, line: entry.line })
    }
    items.push({ kind: 'text', role: 'legend', target: `name:${entry.series}`, x: x + textOffset, y: center + baselineFromCenter(style), text: entry.label, style, anchor: 'start' })
  }
}

// ----------------------------------------------------------- user shapes

function drawShape(shape, items, measure, W, H) {
  const boxX = shape.x1 * W
  const boxY = shape.y1 * H
  const boxW = (shape.x2 - shape.x1) * W
  const boxH = (shape.y2 - shape.y1) * H
  // A turned shape (a:xfrm rot): its anchor is the turned bounding box; the
  // text lays out in the shape's own frame (sides swapped at a quarter
  // turn) and turns with it about the centre.
  const turn = ((((shape.shapeRotation ?? 0) % 360) + 360) % 360)
  const quarter = (turn >= 45 && turn < 135) || (turn >= 225 && turn < 315)
  const w = quarter ? boxH : boxW
  const h = quarter ? boxW : boxH
  const x = boxX + (boxW - w) / 2
  const y = boxY + (boxH - h) / 2
  const cx = boxX + boxW / 2
  const cy = boxY + boxH / 2
  if (shape.fill || shape.line) items.push({ kind: 'rect', x, y, w, h, fill: shape.fill, line: shape.line, ...(turn ? { rotate: turn, ox: cx, oy: cy } : {}) })
  const inset = shape.inset ?? { l: 7.2, t: 3.6, r: 7.2, b: 3.6 }
  const innerW = Math.max(1, w - inset.l - inset.r)
  const block = textBlock(shape.paragraphs, measure, shape.wrap === false ? null : innerW, 'l')
  let top = y + inset.t
  if (shape.anchor === 'ctr') top = y + inset.t + (h - inset.t - inset.b - block.height) / 2
  else if (shape.anchor === 'b') top = y + h - inset.b - block.height
  // A clipping text box (vertOverflow clip/ellipsis) shows its first line
  // whatever its height (chart 04's turned "Hind, €" box is shorter than
  // the line), and after it only the lines that end inside its insets (37).
  const clips = shape.vertOverflow === 'clip' || shape.vertOverflow === 'ellipsis'
  for (const [index, line] of block.lines.entries()) {
    if (clips && index > 0 && (top < y + inset.t - 0.01 || top + line.height > y + h - inset.b + 0.01)) {
      top += line.height
      continue
    }
    const left = line.align === 'ctr' ? x + inset.l + (innerW - line.width) / 2 : line.align === 'r' ? x + inset.l + innerW - line.width : x + inset.l
    let offset = left
    for (const run of line.runs) {
      if (run.text.trim() !== '') {
        items.push({ kind: 'text', role: 'shape', target: shape.key, x: offset, y: top + line.baseline - (run.rise ?? 0), text: run.text, style: run.style, anchor: 'start', ...(turn ? { rotate: turn, ox: cx, oy: cy } : {}) })
      }
      offset += run.width
    }
    top += line.height
  }
}

export { textAscent }
